import { fetcher, InferClientPortalAPI } from "@certn/api";
import { Address, showToast } from "@certn/cascadia";
import {
  BasicButton,
  ButtonGroup,
  Divider,
  PrimaryButton,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import {
  useAllowStripe3dsOverlay,
  confirmSetupAndWaitForPaymentProfile,
} from "@certn/utils";
import {
  useStripe,
  useElements,
  PaymentElement,
  PaymentElementProps,
} from "@stripe/react-stripe-js";
import { PaymentMethodCreateParams } from "@stripe/stripe-js";
import { Form, Formik } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { options } from "./constants";
import { useClientAccount } from "../../hooks/useClientAccount";

interface Props {
  customerAccountId?: string;
  defaultAddress?: Address;
  handleClose?: () => void;
  /**
   * Cancel / dismiss without treating the add as successful. When omitted,
   * Cancel falls back to ``handleClose`` (modal callers that only close UI).
   */
  onCancel?: () => void;
  includeTitle?: boolean;
  title?: string;
  /**
   * Total payment-profile count (API `count`, not page `results.length`)
   * before this add flow. Required so polling cannot treat an existing card
   * list as success when the baseline was omitted (DAT-1731).
   */
  initialPaymentProfileCount: number;
  /**
   * Called when the current SetupIntent is no longer usable (e.g. polling
   * timed out after confirmSetup succeeded). Parent should remint a fresh
   * SetupIntent so the user can try a different card (DAT-1731 Bugbot).
   */
  onSetupIntentConsumed?: (message: string) => void;
  initialErrorMessage?: string | null;
  clientSecret?: string | null;
}

interface FormValues {
  title?: string;
}

export function getPaymentElementOptions(
  defaultAddress?: Address,
  clientPortalExcludeAddressForStripe?: boolean,
  readOnly = false
): PaymentElementProps["options"] {
  const country = defaultAddress?.country;
  return {
    ...options,
    readOnly,
    fields: clientPortalExcludeAddressForStripe
      ? {
          billingDetails: {
            address: "if_required",
          },
        }
      : {},
    ...(country
      ? {
          defaultValues: {
            billingDetails: {
              address: {
                country,
                postal_code: defaultAddress?.postal_code,
              },
            },
          },
        }
      : {}),
  };
}

export function StripeSetupIntentForm(props: Props) {
  const {
    customerAccountId: customerAccountIdProp,
    defaultAddress,
    handleClose,
    onCancel,
    initialPaymentProfileCount,
    onSetupIntentConsumed,
    initialErrorMessage,
    clientSecret,
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const { client } = useClientAccount();
  const customerAccountId = customerAccountIdProp ?? client?.id;
  const [errorMessage, setErrorMessage] = useState<null | string>(
    initialErrorMessage ?? null
  );
  const [isSaving, setIsSaving] = useState(false);
  const { clientPortalExcludeAddressForStripe } = useFlags();
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const isMountedRef = useRef(true);
  useAllowStripe3dsOverlay();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (values: FormValues) => {
    setIsSaving(true);
    setErrorMessage(null);

    if (!stripe || !elements || !isPaymentElementReady) {
      setIsSaving(false);
      return;
    }

    const { title } = values;

    const billingDetails: PaymentMethodCreateParams.BillingDetails = {
      address: {
        // To do this all in one step, we send the title in the address line 2.
        // Backend overwrites all of this address with CustomerAccount.address anyway
        // so this is a safe way to pass info in the absence of Stripe supporting metadata in confirmSetup()
        line2: title,
      },
    };

    try {
      const paymentProfilesUrl = customerAccountId
        ? `/api/internal/client-portal/customer-accounts/${customerAccountId}/payment-profiles/`
        : null;
      type PaymentProfilesResponse =
        InferClientPortalAPI<"/api/internal/client-portal/customer-accounts/{id}/payment-profiles/">;
      const fetchCount = paymentProfilesUrl
        ? async () => {
            const response = await fetcher.request<PaymentProfilesResponse>(
              paymentProfilesUrl,
              { cache: "no-store" }
            );
            return typeof response?.count === "number"
              ? response.count
              : undefined;
          }
        : undefined;

      let baseline = initialPaymentProfileCount;
      if (fetchCount) {
        const currentCount = await fetchCount().catch(() => undefined);
        if (typeof currentCount === "number") baseline = currentCount;
      }

      const outcome = await confirmSetupAndWaitForPaymentProfile({
        stripe,
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: { billing_details: billingDetails },
        },
        clientSecret,
        fetchCount,
        baseline,
        isAborted: () => !isMountedRef.current,
      });

      if (!isMountedRef.current) return;

      if (outcome.status === "created") {
        handleClose?.();
        setIsSaving(false);
        return;
      }
      if (outcome.status === "confirm_error") {
        setErrorMessage(outcome.message || t("Something went wrong"));
        setIsSaving(false);
        return;
      }
      if (!customerAccountId) {
        setErrorMessage(t("Something went wrong"));
        setIsSaving(false);
        return;
      }

      const rejectionMessage = t(
        "This card can't be used. Please try a different card."
      );
      if (onSetupIntentConsumed) {
        onSetupIntentConsumed(rejectionMessage);
        return;
      }
      setErrorMessage(rejectionMessage);
      setIsSaving(false);
    } catch {
      if (!isMountedRef.current) return;
      setErrorMessage(t("Something went wrong"));
      setIsSaving(false);
    }
  };

  const schema = props.includeTitle
    ? object({
        title: string().required().label(t("Title")),
      })
    : object();

  return (
    <Formik<FormValues>
      initialValues={{ title: props.includeTitle ? "" : undefined }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {() => {
        return (
          <Form>
            <Stack gap="space-4">
              {props.includeTitle && (
                <>
                  <TextInputFormikField
                    label={t("Payment profile title")}
                    description={t("Used to identify this payment profile")}
                    testID="payment-profile-title-field"
                    name="title"
                    isRequired
                    isDisabled={isSaving}
                  />
                  <Divider />
                </>
              )}
              <PaymentElement
                options={getPaymentElementOptions(
                  defaultAddress,
                  clientPortalExcludeAddressForStripe,
                  isSaving
                )}
                onReady={() => {
                  setIsPaymentElementReady(true);
                }}
                onLoadError={(error) => {
                  console.error("Stripe PaymentElement failed to load:", error);
                  showToast(
                    t(
                      "Payment form failed to load. Please check your connection, disable ad blockers, and try again."
                    ),
                    { type: "error", timeout: 10000 }
                  );
                  setIsPaymentElementReady(false);
                  setIsSaving(false);
                }}
              />
              {errorMessage && (
                <Text style="form/body-secondary">{errorMessage}</Text>
              )}
              <ButtonGroup justify="end">
                <BasicButton
                  type="button" // Change to button to avoid form submission on cancel
                  size="medium"
                  onPress={onCancel ?? handleClose}
                  testID="add-payment-profile-modal-close-button"
                >
                  {t("Cancel")}
                </BasicButton>
                <PrimaryButton
                  type="submit"
                  size="medium"
                  testID="add-payment-profile-modal-save-button"
                  isDisabled={isSaving || !isPaymentElementReady}
                  isLoading={isSaving}
                >
                  {t("Save")}
                </PrimaryButton>
              </ButtonGroup>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
}
